import React, { useRef } from 'react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

interface FlashCardControlsProps {
  isFlipped: boolean;
  isLastExercise: boolean;
  showExcellentAnimation: boolean;
  onNext: () => void;
  onFail?: () => void;  // New callback for the "Practice more" button
  onLogActivity?: (type: string) => void; // Callback for logging activities
  currentExerciseId?: string; // ID of the current card being displayed
}

/**
 * Action buttons for the flash card exercise
 * 
 * This component always shows two buttons side-by-side:
 * 1. "Practice more" button (left) - marks the card as failed and to be reviewed again
 * 2. "I know this word"/"Complete Level" button (right) - based on current state
 * 
 * Button labels change based on the current state:
 * - "I know this word" is the default label (triggers excellent animation when clicked)
 * - "Complete Level" when on the last exercise
 * - "Excellent!" during the excellent animation (only applies to the right button)
 */
const FlashCardControls: React.FC<FlashCardControlsProps> = ({
  isFlipped,
  isLastExercise,
  showExcellentAnimation,
  onNext,
  onFail,
  onLogActivity,
  currentExerciseId
}) => {
  // Keep track of cards that have already been logged as failed in this level
  // This ref persists between renders but will reset when the component unmounts (level change)
  const loggedFailedCardIds = useRef<Set<string>>(new Set());
  
  return (
    <div className="flex gap-3 w-full mt-4">
      {/* Always show the "Practice more" button */}
      <Button
        variant="outline"
        className="flex-1 border-amber-500 text-amber-600 hover:bg-amber-50"
        onClick={() => {
          // First log the activity, but only if we haven't logged this card before
          if (onLogActivity && currentExerciseId) {
            const alreadyLogged = loggedFailedCardIds.current.has(currentExerciseId);
            
            if (!alreadyLogged) {
              console.log(`Logging failed word activity for card ${currentExerciseId}`);
              loggedFailedCardIds.current.add(currentExerciseId);
              onLogActivity('failed_word');
            } else {
              console.log(`Card ${currentExerciseId} already logged as failed - skipping activity log`);
            }
          } else if (onLogActivity) {
            // Fallback if no card ID provided
            console.log('Logging failed word activity (no card ID provided)');
            onLogActivity('failed_word');
          }
          
          // Then call the original onFail handler
          if (onFail) {
            onFail();
          }
        }}
      >
        Practice more
      </Button>
      
      <Button
        className={cn(
          "flex-1 transition-all duration-200",
          showExcellentAnimation && "bg-green-500 hover:bg-green-600"
        )}
        onClick={onNext}
      >
        {showExcellentAnimation 
          ? "Excellent!" 
          : (isLastExercise ? "Complete Level" : "I know this word")
        }
      </Button>
    </div>
  );
};

export default FlashCardControls;
